import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import {
  BarChart3, BellRing, Bot, Building2, Contact, CornerDownLeft, FileText, Flag, Inbox, Layers, MessageSquareReply, Monitor, Moon, Paperclip,
  PenSquare, Search, Send, Sparkles, Sun,
} from 'lucide-react';
import { useMailboxes } from '../../hooks/useMailboxes';
import { useCategories } from '../../hooks/useCategories';
import { useUpdateSettings } from '../../hooks/useMe';
import { useCurrentWorkspace, useSwitchWorkspace } from '../../hooks/useWorkspace';
import { useAccountFilter } from '../../lib/accountFilter';
import { SETTINGS_SECTIONS } from '../../pages/settings/SettingsLayout';
import { spring } from '../ui/motion';
import { WorkspaceMark } from './WorkspaceSwitcher';

interface Command {
  id: string;
  group: string;
  label: string;
  hint?: string;
  keywords?: string;
  icon: ReactNode;
  run: () => void;
}

const Ctx = createContext<{ open: () => void }>({ open: () => undefined });
export const useCommandPalette = () => useContext(Ctx);

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const PALETTE_SHORTCUT = isMac ? '⌘K' : 'Ctrl K';

/** Every word typed must appear somewhere in the label/keywords (prefix matches rank first). */
function score(c: Command, q: string): number {
  if (!q) return 1;
  const hay = `${c.label} ${c.group} ${c.keywords ?? ''}`.toLowerCase();
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.every((w) => hay.includes(w))) return 0;
  return c.label.toLowerCase().startsWith(words[0]) ? 3 : hay.split(/\s+/).some((t) => t.startsWith(words[0])) ? 2 : 1;
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const value = useMemo(() => ({ open: () => setOpen(true) }), []);
  return (
    <Ctx.Provider value={value}>
      {children}
      <AnimatePresence>{open && <Palette onClose={() => setOpen(false)} />}</AnimatePresence>
    </Ctx.Provider>
  );
}

function Palette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const mailboxes = useMailboxes().data?.mailboxes ?? [];
  const { setAccountId } = useAccountFilter();
  const { workspace, workspaces, isOrg } = useCurrentWorkspace();
  const switchWs = useSwitchWorkspace();
  const update = useUpdateSettings();
  const categories = useCategories().data?.categories ?? [];

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => navigate(to);
    const icon = 'h-4 w-4';
    const list: Command[] = [
      { id: 'compose', group: 'Actions', label: 'Compose new email', keywords: 'write new message', icon: <PenSquare className={icon} />, run: go('/compose') },
      { id: 'inbox', group: 'Go to', label: 'Inbox', icon: <Inbox className={icon} />, run: go('/inbox') },
      { id: 'needs-reply', group: 'Go to', label: 'Needs reply', keywords: 'waiting owe', icon: <MessageSquareReply className={icon} />, run: go('/needs-reply') },
      { id: 'flagged', group: 'Go to', label: 'Flagged', keywords: 'starred', icon: <Flag className={icon} />, run: go('/flagged') },
      { id: 'drafts', group: 'Go to', label: 'Drafts', icon: <FileText className={icon} />, run: go('/drafts') },
      { id: 'sent', group: 'Go to', label: 'Sent', icon: <Send className={icon} />, run: go('/sent') },
      { id: 'review', group: 'Go to', label: 'AI Review', keywords: 'automatic replies sent', icon: <Bot className={icon} />, run: go('/review') },
      { id: 'reminders', group: 'Go to', label: 'Reminders', keywords: 'ringing acknowledge', icon: <BellRing className={icon} />, run: go('/reminders') },
      { id: 'contacts', group: 'Go to', label: 'Contacts', keywords: 'people address book', icon: <Contact className={icon} />, run: go('/contacts') },
      { id: 'library', group: 'Go to', label: 'Attachment library', keywords: 'files documents', icon: <Paperclip className={icon} />, run: go('/library') },
      { id: 'insights', group: 'Go to', label: 'Insights & Ask', keywords: 'analytics question', icon: <BarChart3 className={icon} />, run: go('/insights') },
    ];
    list.push({ id: 'acct-all', group: 'Accounts', label: 'All accounts', keywords: 'unified inbox', icon: <Layers className={icon} />, run: () => (setAccountId(null), navigate('/inbox')) });
    for (const m of mailboxes) {
      list.push({
        id: `acct-${m.id}`,
        group: 'Accounts',
        label: m.displayName,
        hint: m.email,
        keywords: `${m.email} account mailbox`,
        icon: <span className="h-2.5 w-2.5 rounded-full" style={{ background: m.color }} />,
        run: () => (setAccountId(m.id), navigate('/inbox')),
      });
    }
    for (const c of categories) {
      if (c.hidden) continue;
      list.push({
        id: `cat-${c.key}`,
        group: 'Categories',
        label: c.name,
        hint: c.total ? `${c.total} in inbox` : undefined,
        keywords: `category ${c.description}`,
        icon: <span className="h-2.5 w-2.5 rotate-45 rounded-[2px]" style={{ background: c.color }} />,
        run: go(`/inbox?category=${encodeURIComponent(c.key)}`),
      });
    }
    for (const g of SETTINGS_SECTIONS) {
      for (const it of g.items) {
        const Icon = it.icon;
        list.push({ id: `set-${it.path}`, group: 'Settings', label: it.label, keywords: `settings ${g.group}`, icon: <Icon className={icon} />, run: go(`/settings/${it.path}`) });
      }
    }
    for (const w of workspaces) {
      if (w.id === workspace?.id) continue;
      list.push({ id: `ws-${w.id}`, group: 'Workspaces', label: `Switch to ${w.name}`, keywords: 'workspace organization team', icon: <WorkspaceMark ws={w} size={18} />, run: () => switchWs.mutate(w.id) });
    }
    if (isOrg) list.push({ id: 'org', group: 'Workspaces', label: 'Organization: members, divisions & invites', keywords: 'team invite', icon: <Building2 className={icon} />, run: go('/settings/organization') });
    list.push({ id: 'setup', group: 'Actions', label: 'Run first-time setup again', keywords: 'welcome onboarding wizard getting started', icon: <Sparkles className={icon} />, run: () => update.mutate({ onboardedAt: null }, { onSuccess: () => navigate('/welcome') }) });
    list.push(
      { id: 'theme-light', group: 'Appearance', label: 'Light theme', keywords: 'theme mode', icon: <Sun className={icon} />, run: () => update.mutate({ theme: 'light' }) },
      { id: 'theme-dark', group: 'Appearance', label: 'Dark theme', keywords: 'theme mode night', icon: <Moon className={icon} />, run: () => update.mutate({ theme: 'dark' }) },
      { id: 'theme-system', group: 'Appearance', label: 'Match system theme', keywords: 'theme mode auto', icon: <Monitor className={icon} />, run: () => update.mutate({ theme: 'system' }) },
    );
    return list;
  }, [navigate, mailboxes, setAccountId, workspaces, workspace?.id, isOrg, switchWs, update, categories]);

  const q = query.trim();
  const results = useMemo(() => {
    const scored = commands.map((c) => ({ c, s: score(c, q) })).filter((x) => x.s > 0);
    if (q) scored.sort((a, b) => b.s - a.s);
    const out = scored.map((x) => x.c);
    if (q.length >= 2) {
      out.unshift({ id: 'search', group: 'Search', label: `Search mail for “${q}”`, icon: <Search className="h-4 w-4" />, run: () => navigate(`/inbox?q=${encodeURIComponent(q)}`) });
    }
    return out.slice(0, 40);
  }, [commands, q, navigate]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const run = useCallback(
    (c: Command | undefined) => {
      if (!c) return;
      onClose();
      c.run();
    },
    [onClose],
  );

  let lastGroup = '';
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-start justify-center px-3 pt-[12vh]">
      <motion.div className="absolute inset-0 bg-[#0b1220]/40 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label="Search and jump"
        initial={{ opacity: 0, y: -12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={spring}
        className="relative w-full max-w-xl overflow-hidden rounded-panel border border-hairline bg-surface shadow-float"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(results.length - 1, i + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            run(results[active]);
          }
        }}
      >
        <div className="flex items-center gap-3 border-b border-hairline px-4">
          <Search className="h-5 w-5 shrink-0 text-muted" aria-hidden />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search mail, jump to a page, account or setting…"
            aria-label="Search commands"
            aria-controls="palette-results"
            aria-activedescendant={results[active] ? `cmd-${results[active].id}` : undefined}
            className="h-14 min-w-0 flex-1 bg-transparent text-[0.9375rem] text-ink placeholder:text-subtle focus:outline-none"
          />
          <span className="kbd">Esc</span>
        </div>
        <div ref={listRef} id="palette-results" role="listbox" className="max-h-[55vh] overflow-y-auto p-2 scroll-thin">
          {results.length === 0 && <p className="px-3 py-8 text-center text-[0.8125rem] text-muted">Nothing matches “{q}”.</p>}
          {results.map((c, i) => {
            const header = c.group !== lastGroup ? c.group : null;
            lastGroup = c.group;
            return (
              <div key={c.id}>
                {header && <p className="px-3 pb-1 pt-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted first:pt-1">{header}</p>}
                <button
                  id={`cmd-${c.id}`}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  data-index={i}
                  onMouseMove={() => setActive(i)}
                  onClick={() => run(c)}
                  className={clsx('flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[0.8125rem] transition-colors', i === active ? 'bg-primary-soft text-primary-ink' : 'text-ink hover:bg-raised')}
                >
                  <span className={clsx('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', i === active ? 'bg-primary text-primary-fg' : 'bg-raised text-muted')}>{c.icon}</span>
                  <span className="min-w-0 flex-1 truncate font-medium">{c.label}</span>
                  {c.hint && <span className="hidden truncate text-xs text-muted sm:block">{c.hint}</span>}
                  {i === active && <CornerDownLeft className="h-4 w-4 shrink-0" aria-hidden />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 border-t border-hairline bg-sunken/60 px-4 py-2 text-[0.6875rem] text-muted">
          <span className="flex items-center gap-1"><span className="kbd">↑</span><span className="kbd">↓</span> to move</span>
          <span className="flex items-center gap-1"><span className="kbd">↵</span> to open</span>
          <span className="ml-auto flex items-center gap-1"><span className="kbd">{PALETTE_SHORTCUT}</span> anywhere</span>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}
